import { useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../config";
import API from "../api";
import {
  formatAppDateTime,
  getAppDisplayTimezone,
  getAppWallDateParts,
  instantFromAppWallParts,
  resolveAppInstant,
  toMysqlUtcWallString,
} from "../utils/appDateTime";
import { showError } from "../utils/dialog";

import DateTimePickerPanel from "../components/DateTimePicker";
import UnsavedChangesModal from "../components/UnsavedChangesModal";

const NEW_SHIFT_START_DEFAULT_TIME = { hour: 0, minute: 0 };

export default function CreateShiftModal({
  open,
  onClose,
  onSuccess,
  staffRequestId,
  clientLocationName,
  initialData,
  /** Pre-select division from staffing request Default Shift Division */
  defaultDivisionId = "",
  overlayZClass = "z-50",
}) {
  const isDuplicate = Boolean(initialData);
  const getDivisionValue = (data) =>
    data?.divisionId ?? data?.division_id ?? data?.division ?? "";
  const getDivisionOptionId = (div) =>
    div?.division_id ?? div?.id ?? div?.divisionId ?? "";
  const getDivisionOptionName = (div) =>
    div?.name ?? div?.division_name ?? div?.division ?? "";
  const getPositionValue = (data) =>
    data?.positionId ??
    data?.position_id ??
    data?.ponId ??
    data?.position ??
    "";
  const getPositionOptionId = (pos) =>
    pos?.id ?? pos?.position_id ?? pos?.positionId ?? "";
  // archived positions are still returned by the API (so existing shifts /
  // duplicated data referencing them keep working), but they shouldn't be
  // offered as a choice for a new shift.
  const isPositionArchived = (pos) =>
    String(pos?.state || "").toLowerCase() === "archived";
  const [form, setForm] = useState({
    divisionId: "",
    positionId: "",
    startDate: "",
    endDate: "",
    pay_rate: "",
    bill_rate: "",
  });
  const [positionDefaults, setPositionDefaults] = useState({
    unpaid_break: 0,
    uniform: "",
    required_skills: [],
    required_certifications: [],
  });

  const [divisions, setDivisions] = useState([]);
  const [positions, setPositions] = useState([]);
  // we keep a copy of all client positions so we can restore them when the
  // division filter is cleared. `positions` will usually be a subset of
  // `allPositions` after the user picks a division.
  const [allPositions, setAllPositions] = useState([]);
  const [clientId, setClientId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pickerType, setPickerType] = useState(null);
  const [isDirty, setIsDirty] = useState(false);
  const [showUnsavedPopup, setShowUnsavedPopup] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationAlert, setShowValidationAlert] = useState(false);
  const validationTimerRef = useRef(null);
  const submittingRef = useRef(false);

  const endPickerValue = useMemo(() => {
    if (form.endDate || !form.startDate) return form.endDate;
    const start = resolveAppInstant(form.startDate);
    const parts = getAppWallDateParts(start, getAppDisplayTimezone());
    return parts
      ? instantFromAppWallParts(
          { ...parts, hour: 0, minute: 0, second: 0 },
          getAppDisplayTimezone(),
        )
      : "";
  }, [form.endDate, form.startDate]);

  const blankForm = useMemo(
    () => ({
      divisionId: "",
      positionId: "",
      startDate: "",
      endDate: "",
      pay_rate: "",
      bill_rate: "",
    }),
    [],
  );

  // Debug: Log form state changes
  useEffect(() => {
    console.log(
      "CreateShiftModal: Form state changed:",
      JSON.stringify(form, null, 2),
    );
  }, [form]);

  useEffect(() => {
    if (open) {
      // clear out any old data while we pull fresh values
      setPositions([]);
      setAllPositions([]);
      setShowUnsavedPopup(false);
      setValidationErrors({});
      setShowValidationAlert(false);
      fetchDivisionsForModal();
    }
  }, [open]);

  useEffect(() => {
    return () => {
      if (validationTimerRef.current) {
        clearTimeout(validationTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open || !isDirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [open, isDirty]);

  // when we first learn the client id, load the full set of positions for
  // that client (ignoring division). we don’t try to pick a division here –
  // that logic lives in a separate effect which can wait for divisions to be
  // fetched as well.
  useEffect(() => {
    if (clientId) {
      fetchPositions(clientId);
    } else {
      setPositions([]);
      setAllPositions([]);
    }
  }, [clientId]);

  // if there is exactly one division and exactly one position we can pre-
  // select both. the division id is taken from the single position record
  // when available.
  useEffect(() => {
    if (
      clientId &&
      divisions.length === 1 &&
      allPositions.length === 1 &&
      !form.divisionId
    ) {
      const only = allPositions[0];
      const divId =
        only.division_id ?? only.divisionId ?? only.division?.id ?? "";
      if (divId) {
        setForm((prev) => ({ ...prev, divisionId: String(divId) }));
      }
    }
  }, [clientId, divisions, allPositions, form.divisionId]);

  // Handle initial data for duplication
  useEffect(() => {
    console.log(
      "CreateShiftModal useEffect triggered - open:",
      open,
      "initialData:",
      initialData,
    );

    if (open && initialData) {
      console.log(
        "CreateShiftModal: Received initial data for duplication:",
        JSON.stringify(initialData, null, 2),
      );

      // Set form with initial data immediately (dates and rates don't need divisions to be loaded)
      const newForm = {
        divisionId: String(getDivisionValue(initialData) || ""),
        positionId: String(getPositionValue(initialData) || ""),
        startDate: initialData.startDate || "",
        endDate: initialData.endDate || "",
        pay_rate: String(initialData.payRate || ""),
        bill_rate: String(initialData.billRate || ""),
      };

      console.log(
        "CreateShiftModal: Setting form to:",
        JSON.stringify(newForm, null, 2),
      );
      setForm(newForm);
      setIsDirty(false);
      setValidationErrors({});
    } else if (open && !initialData) {
      console.log("CreateShiftModal: Resetting form for new shift creation");
      const divisionDefault = String(defaultDivisionId || "").trim();
      setForm({
        divisionId: divisionDefault,
        positionId: "",
        startDate: "",
        endDate: "",
        pay_rate: "",
        bill_rate: "",
      });
      setPositionDefaults({
        unpaid_break: 0,
        uniform: "",
        required_skills: [],
        required_certifications: [],
      });
    }
  }, [blankForm, open, initialData, defaultDivisionId]);

  // Apply staffing-request division default once divisions list is available
  useEffect(() => {
    if (!open || initialData) return;
    const divisionDefault = String(defaultDivisionId || "").trim();
    if (!divisionDefault || divisions.length === 0) return;
    const exists = divisions.some(
      (div) => String(getDivisionOptionId(div)) === divisionDefault,
    );
    if (!exists) return;
    setForm((prev) => {
      if (prev.divisionId) return prev;
      return { ...prev, divisionId: divisionDefault };
    });
  }, [open, initialData, defaultDivisionId, divisions]);

  // Separate effect to handle positions when division is set and divisions are loaded
  useEffect(() => {
    async function loadPositionsForDivision() {
      if (!form.divisionId || divisions.length === 0) return;
      console.log(
        "CreateShiftModal: Fetching positions for division:",
        form.divisionId,
      );

      // Check if the division ID exists in available divisions
      const divisionExists = divisions.find(
        (div) => String(getDivisionOptionId(div)) === String(form.divisionId),
      );
      console.log(
        "CreateShiftModal: Division exists check:",
        divisionExists ? "Found" : "Not found",
        "for ID:",
        form.divisionId,
      );
      console.log(
        "CreateShiftModal: Available division IDs:",
        divisions.map((d) => getDivisionOptionId(d)),
      );

      if (clientId) {
        await fetchPositions(clientId, form.divisionId);
      } else {
        console.error(
          "Could not determine client ID for staff request:",
          staffRequestId,
        );
        setPositions([]);
      }
    }

    loadPositionsForDivision();
  }, [form.divisionId, divisions, staffRequestId, clientId]);

  // Effect to auto-populate rates and position defaults when position changes or positions are loaded
  useEffect(() => {
    let cancelled = false;

    async function loadPositionDefaults() {
      console.log(
        "Auto-populate effect triggered: positionId:",
        form.positionId,
        "positions.length:",
        positions.length,
      );
      if (!form.positionId || positions.length === 0) return;

      const selectedPosition = positions.find(
        (pos) => String(getPositionOptionId(pos)) === String(form.positionId),
      );
      console.log("Auto-populate effect: selectedPosition:", selectedPosition);

      if (!selectedPosition) {
        console.log(
          "Auto-populate effect: position not found in positions array",
        );
        return;
      }

      const payRate = selectedPosition.pay_rate || "";
      const billRate = selectedPosition.bill_rate || "";
      setForm((prev) => {
        if (
          prev.pay_rate !== String(payRate) ||
          prev.bill_rate !== String(billRate)
        ) {
          return {
            ...prev,
            pay_rate: payRate ? String(payRate) : "",
            bill_rate: billRate ? String(billRate) : "",
          };
        }
        return prev;
      });

      const positionId = getPositionOptionId(selectedPosition);
      if (!positionId) return;

      try {
        const detailRes = await API.get(`/api/clients/positions/${positionId}`);
        const detail = detailRes?.data?.data || selectedPosition || {};
        if (cancelled) return;
        setPositionDefaults({
          unpaid_break: Number(detail.unpaid_break || 0),
          uniform: String(detail.uniform || ""),
          required_skills: Array.isArray(detail.skill_ids)
            ? detail.skill_ids
            : [],
          required_certifications: Array.isArray(detail.certification_ids)
            ? detail.certification_ids
            : [],
        });
      } catch (error) {
        console.error("Error loading position defaults:", error);
        if (cancelled) return;
        setPositionDefaults({
          unpaid_break: Number(selectedPosition.unpaid_break || 0),
          uniform: String(selectedPosition.uniform || ""),
          required_skills: Array.isArray(selectedPosition.skill_ids)
            ? selectedPosition.skill_ids
            : [],
          required_certifications: Array.isArray(
            selectedPosition.certification_ids,
          )
            ? selectedPosition.certification_ids
            : [],
        });
      }
    }

    loadPositionDefaults();
    return () => {
      cancelled = true;
    };
  }, [form.positionId, positions]);

  const fetchDivisionsForModal = async () => {
    try {
      // For shift creation under a staffing request, divisions should be client-scoped.
      const resolvedClientId = staffRequestId
        ? await fetchClientId(staffRequestId)
        : null;
      setClientId(resolvedClientId || null);

      let divisionsData = [];
      if (resolvedClientId) {
        try {
          const response = await API.get(
            `/api/clients/divisions/by-client/${resolvedClientId}`,
          );
          divisionsData = response.data.data || [];
        } catch (err) {
          // Fallback: alternate route shape used in some installs
          const response = await API.get(
            `/api/clients/${resolvedClientId}/divisions`,
          );
          divisionsData = response.data.data || [];
        }
        console.log(
          "CreateShiftModal: Fetched client divisions:",
          JSON.stringify(divisionsData, null, 2),
        );
      }

      // Fallback for older API mappings
      if (!Array.isArray(divisionsData) || divisionsData.length === 0) {
        const fallback = await API.get("/api/divisions");
        divisionsData = fallback.data.data || [];
        console.log(
          "CreateShiftModal: Fallback divisions:",
          JSON.stringify(divisionsData, null, 2),
        );
      }

      setDivisions(divisionsData);
    } catch (error) {
      console.error("Error fetching divisions:", error);
      setDivisions([]);
      setClientId(null);
    }
  };

  const fetchPositions = async (clientId, divisionId) => {
    try {
      // PDF-compliant: Get positions that are valid for the specified client,
      // optionally narrowed to a division. When divisionId is undefined we use
      // the returned list as the master set of all client positions so the
      // dropdown can work even if the user hasn’t chosen a division yet.
      const params = divisionId ? { division_id: divisionId } : {};
      const response = await API.get(`/api/clients/${clientId}/positions`, {
        params,
      });
      const rawData = response.data.data || [];

      // remove duplicates by normalized ID (the duplicates seen in the
      // screenshot were coming from the backend returning the same position
      // row multiple times). dedup guarantees the auto‑select logic can work
      // when there’s really only one choice.
      const seen = new Set();
      const positionsData = rawData.filter((p) => {
        const id = getPositionOptionId(p);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });

      console.log(
        "CreateShiftModal: Fetched client positions (deduped):",
        positionsData.length,
        "for client",
        clientId,
        "division",
        divisionId,
      );
      console.log(
        "CreateShiftModal: RAW Positions data:",
        JSON.stringify(rawData, null, 2),
      );
      console.log(
        "CreateShiftModal: Positions mapped:",
        positionsData.map((p) => ({
          id: getPositionOptionId(p),
          name: p.name,
          pay_rate: p.pay_rate,
          bill_rate: p.bill_rate,
        })),
      );

      if (!divisionId) {
        // master list for the client
        setAllPositions(positionsData);
      }

      // update the currently visible dropdown options
      setPositions(positionsData);

      // helper: if there is exactly one choice we auto‑select it (regardless of
      // whether we just fetched the master list or subdivision-specific results)
      // — but never auto-select an archived position for a new shift.
      if (
        positionsData.length === 1 &&
        !form.positionId &&
        !isPositionArchived(positionsData[0])
      ) {
        const singleId = getPositionOptionId(positionsData[0]);
        if (singleId) {
          setForm((prev) => ({ ...prev, positionId: String(singleId) }));
        }
      }
    } catch (error) {
      console.error("Error fetching positions:", error);
      setPositions([]);
    }
  };

  const fetchClientId = async (staffRequestId) => {
    try {
      const response = await API.get(
        `/api/jobs/staffingrequests/${staffRequestId}`,
      );
      const staffRequest = response.data?.data || response.data;
      console.log("CreateShiftModal: Fetched staff request:", staffRequest);
      return staffRequest?.client_id;
    } catch (error) {
      console.error("Error fetching staff request:", error);
      return null;
    }
  };

  const handleDivisionChange = async (e) => {
    const divisionId = e.target.value;
    setIsDirty(true);
    setValidationErrors((prev) => ({
      ...prev,
      divisionId: "",
      positionId: "",
      pay_rate: "",
      bill_rate: "",
    }));
    setForm((prev) => ({
      ...prev,
      divisionId,
      positionId: "",
      pay_rate: "",
      bill_rate: "",
    }));
    setPositionDefaults({
      unpaid_break: 0,
      uniform: "",
      required_skills: [],
      required_certifications: [],
    });

    if (clientId) {
      if (divisionId) {
        // narrow the list to division-specific roles
        fetchPositions(clientId, divisionId);
      } else {
        // clear the filter and show every position for the client again
        setPositions(allPositions);
      }
    } else {
      setPositions([]);
    } 
  };

  const handlePositionChange = (e) => {
    const positionId = e.target.value;
    setIsDirty(true);
    setValidationErrors((prev) => ({
      ...prev,
      positionId: "",
      pay_rate: "",
      bill_rate: "",
    }));
    setForm((prev) => {
      const newForm = { ...prev, positionId };

      // Clear rates when no position is selected
      if (!positionId) {
        newForm.pay_rate = "";
        newForm.bill_rate = "";
      }

      return newForm;
    });
  };

  const triggerValidationAlert = () => {
    setShowValidationAlert(true);
    if (validationTimerRef.current) {
      clearTimeout(validationTimerRef.current);
    }
    validationTimerRef.current = setTimeout(() => {
      setShowValidationAlert(false);
      validationTimerRef.current = null;
    }, 4000);
  };

  const validateForm = () => {
    const errors = {};

    if (!form.divisionId) errors.divisionId = "This field is required";
    if (!form.positionId) errors.positionId = "This field is required";
    if (!form.startDate) errors.startDate = "This field is required";
    if (!form.endDate) errors.endDate = "This field is required";
    if (
      form.startDate &&
      form.endDate &&
      new Date(form.endDate).getTime() <= new Date(form.startDate).getTime()
    ) {
      errors.endDate = "End date and time must be after start";
    }
    if (!form.pay_rate) errors.pay_rate = "This field is required";
    if (!form.bill_rate) errors.bill_rate = "This field is required";

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      triggerValidationAlert();
      return false;
    }

    setShowValidationAlert(false);
    return true;
  };

  const submitForm = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    try {
      const response = await API.post("/api/jobs/shifts", {
        staff_request_id: staffRequestId,
        division_id: form.divisionId,
        position_id: form.positionId,
        start_date: toMysqlUtcWallString(form.startDate),
        end_date: toMysqlUtcWallString(form.endDate),
        pay_rate: parseFloat(form.pay_rate || 0),
        bill_rate: parseFloat(form.bill_rate || 0),
        unpaid_break: Number(positionDefaults.unpaid_break || 0),
        uniform: positionDefaults.uniform || "",
        required_skills: Array.isArray(positionDefaults.required_skills)
          ? positionDefaults.required_skills
          : [],
        required_certifications: Array.isArray(
          positionDefaults.required_certifications,
        )
          ? positionDefaults.required_certifications
          : [],
      });

      if (!response.data.success) {
        throw new Error(response.data.message || "Failed to create shift");
      }

      // Reset form
      setForm({
        divisionId: "",
        positionId: "",
        startDate: "",
        endDate: "",
        pay_rate: "",
        bill_rate: "",
      });
      setPositionDefaults({
        unpaid_break: 0,
        uniform: "",
        required_skills: [],
        required_certifications: [],
      });

      // Call onSuccess if provided
      if (onSuccess) {
        onSuccess(response.data);
      }

      onClose();
    } catch (error) {
      console.error("Error creating shift:", error);
      // Show an error to the user
      showError(error?.message || "Failed to create shift");
    } finally {
      setLoading(false);
      submittingRef.current = false;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (submittingRef.current) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    await submitForm();
  };

  const resetFormState = () => {
    setForm(blankForm);
    setPositions([]);
    setAllPositions([]);
    setClientId(null);
    setPickerType(null);
    setValidationErrors({});
    setShowValidationAlert(false);
  };

  const handleAttemptClose = () => {
    if (loading) return;
    setPickerType(null);

    if (isDirty) {
      setShowUnsavedPopup(true);
      return;
    }

    onClose();
  };

  const handleSaveContinue = () => {
    setShowUnsavedPopup(false);

    if (submittingRef.current) {
      return;
    }

    if (!validateForm()) {
      return;
    }

    submitForm();
  };

  const handleDiscardChanges = () => {
    resetFormState();
    setIsDirty(false);
    setShowUnsavedPopup(false);
    onClose();
  };

  const handleReviewChanges = () => {
    setShowUnsavedPopup(false);
  };

  if (!open) return null;

  return (
    <div
      className={`fixed inset-0 bg-black/40 flex items-center justify-center ${overlayZClass}`}
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) {
          handleAttemptClose();
        }
      }}
    >
      <div
        className="bg-white rounded-lg shadow w-full max-w-lg p-6"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold">
            {isDuplicate ? "Duplicate Shift" : "Create Shift"}
          </h3>
          <button
            className="text-gray-500 hover:text-gray-700"
            onClick={handleAttemptClose}
          >
            ✖
          </button>
        </div>

        <div className="text-sm text-gray-500 mb-6">{clientLocationName}</div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label
              className={`block text-sm font-medium ${
                validationErrors.divisionId ? "text-red-500" : "text-gray-700"
              }`}
            >
              Division <span className="text-red-500">*</span>
            </label>
            <select
              className={`mt-1 w-full rounded-lg px-3 py-2 ${
                validationErrors.divisionId
                  ? "border border-red-500 text-red-500"
                  : "border border-gray-300"
              }`}
              value={form.divisionId}
              onChange={handleDivisionChange}
            >
              <option value="">Select Division</option>
              {divisions.map((div) => (
                <option
                  key={getDivisionOptionId(div)}
                  value={getDivisionOptionId(div)}
                >
                  {getDivisionOptionName(div)}
                </option>
              ))}
            </select>
            {validationErrors.divisionId && (
              <p className="mt-1 text-xs text-red-500">
                {validationErrors.divisionId}
              </p>
            )}
          </div>

          <div>
            <label
              className={`block text-sm font-medium ${
                validationErrors.positionId ? "text-red-500" : "text-gray-700"
              }`}
            >
              Position <span className="text-red-500">*</span>
            </label>
            <select
              className={`mt-1 w-full rounded-lg px-3 py-2 ${
                validationErrors.positionId
                  ? "border border-red-500 text-red-500"
                  : "border border-gray-300"
              }`}
              value={form.positionId}
              onChange={handlePositionChange}
              // allow picking a position even if no division has been chosen;
              // if there are no options the dropdown will already be empty
              disabled={positions.length === 0}
            >
              <option value="">Select Position</option>
              {positions
                .filter((pos) => {
                  const pid = getPositionOptionId(pos);
                  // hide archived positions from selection, but keep whichever
                  // one is already chosen (e.g. from duplicated shift data)
                  // visible so the field doesn't appear to silently clear.
                  return (
                    !isPositionArchived(pos) ||
                    String(pid) === String(form.positionId)
                  );
                })
                .map((pos) => {
                  const pid = getPositionOptionId(pos);
                  const label = isPositionArchived(pos)
                    ? `${pos.name} (Archived)`
                    : pos.name;
                  return (
                    <option key={pid} value={pid}>
                      {label}
                    </option>
                  );
                })}
            </select>
            {validationErrors.positionId && (
              <p className="mt-1 text-xs text-red-500">
                {validationErrors.positionId}
              </p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
  <label
    className={`block text-sm font-medium ${
      validationErrors.startDate ? "text-red-500" : "text-gray-700"
    }`}
  >
    Start <span className="text-red-500">*</span>
  </label>

  <input
    readOnly
    value={form.startDate ? formatAppDateTime(form.startDate) : ""}
    onClick={() => setPickerType("start")}
    className={`mt-1 w-full rounded-lg px-3 py-2 cursor-pointer ${
      validationErrors.startDate
        ? "border border-red-500 text-red-500"
        : "border border-gray-300"
    }`}
    placeholder="Select start date & time"
  />
  {validationErrors.startDate && (
    <p className="mt-1 text-xs text-red-500">{validationErrors.startDate}</p>
  )}
</div>

            <div>
  <label
    className={`block text-sm font-medium ${
      validationErrors.endDate ? "text-red-500" : "text-gray-700"
    }`}
  >
    End <span className="text-red-500">*</span>
  </label>

  <input
    readOnly
    value={form.endDate ? formatAppDateTime(form.endDate) : ""}
    onClick={() => setPickerType("end")}
    className={`mt-1 w-full rounded-lg px-3 py-2 cursor-pointer ${
      validationErrors.endDate
        ? "border border-red-500 text-red-500"
        : "border border-gray-300"
    }`}
    placeholder="Select end date & time"
  />
  {validationErrors.endDate && (
    <p className="mt-1 text-xs text-red-500">{validationErrors.endDate}</p>
  )}
</div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label
                className={`block text-sm font-medium ${
                  validationErrors.pay_rate ? "text-red-500" : "text-gray-700"
                }`}
              >
                Pay Rate ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                className={`mt-1 w-full rounded-lg px-3 py-2 ${
                  validationErrors.pay_rate
                    ? "border border-red-500 text-red-500"
                    : "border border-gray-300"
                }`}
                value={form.pay_rate ?? ""}
                onChange={(e) => {
                  setIsDirty(true);
                  setValidationErrors((prev) => ({
                    ...prev,
                    pay_rate: "",
                  }));
                  setForm((prev) => ({ ...prev, pay_rate: e.target.value }));
                }}
                placeholder="0.00"
              />
              {validationErrors.pay_rate && (
                <p className="mt-1 text-xs text-red-500">
                  {validationErrors.pay_rate}
                </p>
              )}
            </div>

            <div>
              <label
                className={`block text-sm font-medium ${
                  validationErrors.bill_rate ? "text-red-500" : "text-gray-700"
                }`}
              >
                Bill Rate ($) <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                step="0.01"
                className={`mt-1 w-full rounded-lg px-3 py-2 ${
                  validationErrors.bill_rate
                    ? "border border-red-500 text-red-500"
                    : "border border-gray-300"
                }`}
                value={form.bill_rate ?? ""}
                onChange={(e) => {
                  setIsDirty(true);
                  setValidationErrors((prev) => ({
                    ...prev,
                    bill_rate: "",
                  }));
                  setForm((prev) => ({ ...prev, bill_rate: e.target.value }));
                }}
                placeholder="0.00"
              />
              {validationErrors.bill_rate && (
                <p className="mt-1 text-xs text-red-500">
                  {validationErrors.bill_rate}
                </p>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-6">
            <button
              type="button"
              onClick={handleAttemptClose}
              className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              {loading ? "Creating..." : "Create"}
            </button>
          </div>
        </form>

      {pickerType && (
  <DateTimePickerPanel
    value={pickerType === "start" ? form.startDate : endPickerValue}
    mode={pickerType === "start" ? "from" : "to"}
    defaultTime={
      !isDuplicate && (pickerType === "start" || pickerType === "end")
        ? NEW_SHIFT_START_DEFAULT_TIME
        : null
    }
    minDateTime={
      pickerType === "end" && form.startDate ? form.startDate : null
    }
    onChange={(date) => {
      if (pickerType === "start") {
        setIsDirty(true);
        setValidationErrors((prev) => ({
          ...prev,
          startDate: "",
          endDate: "",
        }));
        setForm((prev) => ({
          ...prev,
          startDate: date,
          endDate: "",
        }));
      } else {
        setIsDirty(true);
        setValidationErrors((prev) => ({
          ...prev,
          endDate: "",
        }));
        setForm((prev) => ({
          ...prev,
          endDate: date,
        }));
      }
    }}

    onDone={() => setPickerType(null)}

    onContinue={(date) => {
      setIsDirty(true);
      setValidationErrors((prev) => ({
        ...prev,
        startDate: "",
      }));
      setForm((prev) => ({
        ...prev,
        startDate: date,
      }));
      setPickerType("end");
    }}

    onBack={() => setPickerType("start")}
  />
)}

      <UnsavedChangesModal
        open={showUnsavedPopup}
        onSave={handleSaveContinue}
        onDiscard={handleDiscardChanges}
        onReview={handleReviewChanges}
      />

      {showValidationAlert && (
        <div className="fixed bottom-5 right-5 z-[120] rounded bg-[#f47d6b] px-5 py-4 text-sm font-medium text-white shadow-xl">
          Please review errors highlighted in red
        </div>
      )}

      </div>
    </div>
  );
}
